import { useMemo, type CSSProperties } from 'react'
import type { Item, Member } from '../../lib/supabase'
import type { Tab } from '../shell/AppShell'
import type { Cover as CoverModel } from '../../lib/recipeBook'
import { pickPulse, pulseLines, type PulseInput } from '../../lib/pulse'
import { whenLabel, type LastTrip } from '../../lib/lastTrip'
import { clock } from '../../lib/trips'
import { formatCost } from '../../lib/currency'
import { MARK_PATH, MARK_STROKE, MARK_TRANSFORM } from '../../lib/brandMark'
import { BasketIcon, ChevronIcon, ClipboardIcon, PlusIcon } from '../icons'
import { Cover } from '../recipes/Cover'

// Home (the phone's first page, before List · Recipes · Plan): today's line
// about the household (lib/pulse.ts), then a card per thing that matters now —
// the list with Start shopping, tonight's meal with Cook, the last trip
// (lib/lastTrip.ts: where, items, time in store, spent, the aisles and the
// usual trip; a tap opens its summary, and a trip with no cost has Add cost,
// which opens Edit trip — the watch asks no cost), and a recipe tile whose Paste imports
// a copied link straight into Recipes. Every block is a way into its section;
// Stats is the button beside the people. On Free, tonight gives way to one
// card on the ink band that says what Plus adds; the last trip stays, since
// trips are Free.

const FIRST_ITEMS = 3
const LEGEND = 3

export interface HomeProps {
  members: Member[]
  selfMemberId: string | null
  /** Stats' numbers (lib/stats.ts); null while history and trips load. */
  stats: PulseInput['stats'] | null
  now: Date
  /** What is still to get. */
  open: Item[]
  /** A trip is open in the household, whoever is on it. */
  tripOpen: boolean
  /** The list card's button: Start shopping, Continue shopping, or Join Anna (lib/sharedTrip.ts). */
  shopLabel: string
  tonight: {
    mealId: string; title: string; servings: number; minutes: number | null; cooked: boolean
    cover: CoverModel; photoUrl?: string | null
  } | null
  /** The newest finished trip; null before the first, undefined while it loads. */
  lastTrip: LastTrip | null | undefined
  /** Free: open places left under the item cap (lib/billing.ts freeItemsLeft); null on Plus. */
  freeLeft: number | null
  tintFor: (categoryId: string | null) => string
  nameOf: (categoryId: string | null) => string
  plus: boolean
  onOpen: (tab: Tab) => void
  onStartShopping: () => void
  /** Someone else's trip is open: End trip, for everyone, after a question (on the list). */
  onEndTrip?: () => void
  onCook: (mealId: string) => void
  onOpenTrip: () => void
  /** The last trip's Add cost (it has a row and no cost yet): Edit trip, the cost first. */
  onAddCost: () => void
  onUpgrade: () => void
  /** The recipe tile's Paste: what the clipboard held, read on the tap (iPhone asks to allow it). */
  onPasteRecipe: (text: string) => void
  /** The recipe tile's More ways: Recipes' Add a recipe sheet. */
  onAddRecipe: () => void
}

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

export function HomeTab(p: HomeProps) {
  const pulse = useMemo(() => (p.stats
    ? pickPulse(pulseLines({ stats: p.stats, members: p.members, selfMemberId: p.selfMemberId, now: p.now }), p.now)
    : null), [p.stats, p.members, p.selfMemberId, p.now])

  // "2 added by Emma today": who else put things on the list today, the most first.
  const addedToday = useMemo(() => {
    const counts = new Map<string, number>()
    for (const i of p.open) {
      if (i.created_by && i.created_by !== p.selfMemberId && sameDay(new Date(i.created_at), p.now)) {
        counts.set(i.created_by, (counts.get(i.created_by) ?? 0) + 1)
      }
    }
    const [id, n] = [...counts].sort((a, b) => b[1] - a[1])[0] ?? []
    const name = id ? p.members.find(m => m.id === id)?.display_name : null
    return name && n ? `${n} added by ${name} today` : null
  }, [p.open, p.members, p.selfMemberId, p.now])

  // Read on the tap itself: iPhone allows a clipboard read only inside the gesture.
  async function pasteRecipe() {
    let text = ''
    try { text = await navigator.clipboard.readText() } catch { /* denied or unsupported */ }
    p.onPasteRecipe(text)
  }

  const first = p.open.slice(0, FIRST_ITEMS)
  const more = p.open.length - first.length
  const empty = p.open.length === 0 && !p.tripOpen
  const subline = p.freeLeft != null ? `${p.freeLeft} ${p.freeLeft === 1 ? 'place' : 'places'} left on Free` : addedToday

  const lastTrip = p.lastTrip !== undefined && (
    <LastTripCard trip={p.lastTrip} now={p.now} tintFor={p.tintFor} nameOf={p.nameOf} onOpen={p.onOpenTrip}
      onAddCost={p.lastTrip?.tripId && !p.lastTrip.cost ? p.onAddCost : undefined} />
  )

  return (
    <div className="ml-home">
      {pulse && (
        <div className="ml-home__pulse">
          <h1 className="ml-home__line">{pulse.lead}<mark>{pulse.mark}</mark>{pulse.tail}</h1>
          {pulse.sub && <p className="ml-home__sub">{pulse.sub}</p>}
        </div>
      )}

      <section className="ml-home__list" aria-label="List" data-tour="home-list">
        <div className="ml-home__list-top">
          <div className="ml-home__count">
            <span className="ml-home__num ml-num">{p.open.length}</span>
            <span className="ml-home__count-text">
              {p.open.length ? 'to get' : 'Nothing on the list yet'}
              {p.open.length > 0 && subline && <small>{subline}</small>}
            </span>
          </div>
          <button type="button" className="ml-home__open" aria-label="Open the list" onClick={() => p.onOpen('list')}>
            <span className="ml-label">List</span><ChevronIcon size={14} />
          </button>
        </div>
        {first.length > 0 && (
          <ul className="ml-home__chips">
            {first.map(i => (
              <li key={i.id} style={{ '--ml-tint': p.tintFor(i.category_id) } as CSSProperties}>
                <span className="ml-home__chip-dot" aria-hidden="true" />{i.name}
              </li>
            ))}
            {more > 0 && <li>+{more}</li>}
          </ul>
        )}
        {empty
          ? (
            <button type="button" className="ml-home__shop" onClick={() => p.onOpen('list')}>
              <PlusIcon size={18} />Add the first item
            </button>
          )
          : (
            <button type="button" className="ml-home__shop" onClick={p.onStartShopping}>
              <BasketIcon size={18} />{p.shopLabel}
            </button>
          )}
        {p.onEndTrip && (
          <button type="button" className="ml-home__end" onClick={p.onEndTrip}>End trip</button>
        )}
      </section>

      {p.plus ? (
        <>
          <section className="ml-home__card" aria-label="Tonight">
            <span className="ml-label">Tonight</span>
            {p.tonight
              ? (
                <div className="ml-home__meal">
                  <Cover cover={p.tonight.cover} photoUrl={p.tonight.photoUrl} tintFor={p.tintFor} size="thumb" />
                  <span className="ml-home__meal-text">
                    <b>{p.tonight.title}</b>
                    <small>{[`${p.tonight.servings} servings`, p.tonight.minutes ? `${p.tonight.minutes} min` : null].filter(Boolean).join(', ')}</small>
                  </span>
                  <button type="button" className="ml-home__cook" aria-label={`Cook ${p.tonight.title}`}
                    onClick={() => p.onCook(p.tonight!.mealId)}>
                    {p.tonight.cooked ? 'Again' : 'Cook'}
                  </button>
                </div>
              )
              : (
                <div className="ml-home__meal">
                  <span className="ml-home__meal-text">
                    <b>Nothing planned</b>
                    <small>Pick a recipe for tonight</small>
                  </span>
                  <button type="button" className="ml-home__ghost" onClick={() => p.onOpen('meals')}>Plan tonight</button>
                </div>
              )}
          </section>
          {lastTrip}
        </>
      ) : (
        <>
          {lastTrip}
          <section className="ml-home__plus" aria-label="Plus">
            <svg className="ml-home__plus-mark" viewBox="0 0 100 100" aria-hidden="true">
              <path d={MARK_PATH} transform={MARK_TRANSFORM} strokeWidth={MARK_STROKE} />
            </svg>
            <span className="ml-home__plus-pill">Plus</span>
            <h2 className="ml-home__plus-title">Plan dinner. Shop it in one tap.</h2>
            <ul className="ml-home__plus-list">
              <li>Tonight’s meal and the week’s plan</li>
              <li>Unlimited items, ticking on the watch</li>
              <li>Recipe import and Running low</li>
            </ul>
            <button type="button" className="ml-home__plus-cta" onClick={p.onUpgrade}>Get Plus</button>
          </section>
        </>
      )}

      <section className="ml-home__card ml-home__recipe" aria-label="Add a recipe">
        <span className="ml-home__recipe-text">
          <b>Got a recipe?</b>
          <small>{p.plus ? 'Paste a link, we’ll do the rest.' : 'Saved recipes stay readable on Free.'}</small>
        </span>
        <span className="ml-home__recipe-actions">
          <button type="button" className="ml-home__paste" onClick={() => void pasteRecipe()}>
            <ClipboardIcon size={16} /> Paste
          </button>
          <button type="button" className="ml-home__ghost" onClick={p.onAddRecipe}>More ways</button>
        </span>
      </section>
    </div>
  )
}

function LastTripCard({ trip, now, tintFor, nameOf, onOpen, onAddCost }: {
  trip: LastTrip | null
  now: Date
  tintFor: (categoryId: string | null) => string
  nameOf: (categoryId: string | null) => string
  onOpen: () => void
  onAddCost?: () => void
}) {
  if (!trip) {
    return (
      <section className="ml-home__card" aria-label="Last trip">
        <span className="ml-label">Last trip</span>
        <div className="ml-home__trip-empty">
          <span className="ml-home__trip-icon" aria-hidden="true"><BasketIcon size={20} /></span>
          <span className="ml-home__meal-text">
            <b>Your first trip shows up here</b>
            <small>Time in store, what it cost, aisle by aisle.</small>
          </span>
        </div>
      </section>
    )
  }
  const r = trip.recap
  const delta = r.ms !== null && r.usualMs !== null ? Math.round((r.ms - r.usualMs) / 60_000) : null
  const splits = r.splits.filter(s => s.items > 0)
  const named = splits.map(s => `${nameOf(s.categoryId)} ${s.items}`)
  return (
    <section className="ml-home__card ml-home__trip" aria-label="Last trip">
      <button type="button" className="ml-home__fill" aria-label="Open the last trip" onClick={onOpen} />
      <span className="ml-home__trip-head"><span className="ml-label">Last trip</span><ChevronIcon size={14} /></span>
      <b className="ml-home__trip-where">{[trip.storeName, whenLabel(trip.finishedAt, now)].filter(Boolean).join(', ')}</b>
      <dl className="ml-home__nums">
        <div><dt>Items</dt><dd className="ml-num">{r.items}</dd></div>
        <div><dt>In store</dt><dd className="ml-num">{r.ms !== null ? clock(r.ms) : '—'}</dd></div>
        <div>
          <dt>Spent</dt>
          {onAddCost
            ? <dd><button type="button" className="ml-add-cost" onClick={onAddCost}>Add cost</button></dd>
            : <dd className="ml-num">{trip.cost ? formatCost(trip.cost.amount, trip.cost.currency) : '—'}</dd>}
        </div>
      </dl>
      {splits.length > 0 && (
        <span className="ml-home__splits" role="img" aria-label={named.join(', ')}>
          {splits.map((s, i) => <i key={i} style={{ flexGrow: s.items, background: tintFor(s.categoryId) }} />)}
        </span>
      )}
      {delta !== null
        ? (
          <p className="ml-home__versus">
            {delta === 0 ? 'About as long as your usual trip' : <><em>{Math.abs(delta)} min</em> {delta < 0 ? 'faster' : 'longer'} than your usual trip</>}
          </p>
        )
        : splits.length > 0 && (
          <ul className="ml-home__legend" aria-hidden="true">
            {splits.slice(0, LEGEND).map((s, i) => (
              <li key={i} style={{ '--ml-tint': tintFor(s.categoryId) } as CSSProperties}>{nameOf(s.categoryId)} {s.items}</li>
            ))}
          </ul>
        )}
    </section>
  )
}
